import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Send as TestIcon
} from '@mui/icons-material';
import axios from '../utils/axiosConfig';

interface MailConfig {
  _id?: string;
  name: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword?: string;
  fromEmail: string;
  fromName: string;
  replyTo?: string;
  isActive: boolean;
  isDefault: boolean;
  organizerId?: string;
  competitionId?: string;
}

interface MailConfigTabProps {
  organizerId: string;
  onMessage: (message: string) => void;
  onError: (error: string) => void;
}

const MailConfigTab: React.FC<MailConfigTabProps> = ({ 
  organizerId, 
  onMessage, 
  onError 
}) => {
  const [mailConfigs, setMailConfigs] = useState<MailConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<MailConfig | null>(null);
  const [testingConfig, setTestingConfig] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');

  const getEmptyConfig = (): MailConfig => ({
    name: '',
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: '',
    replyTo: '',
    isActive: true,
    isDefault: false,
    organizerId
  });

  useEffect(() => {
    if (organizerId) {
      fetchMailConfigs();
    }
  }, [organizerId]);

  const fetchMailConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/mail-config?organizerId=${organizerId}`);
      setMailConfigs(response.data);
    } catch (error) {
      onError('Fehler beim Laden der Mail-Konfigurationen');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingConfig(getEmptyConfig());
    setShowDialog(true);
  };

  const handleEdit = (config: MailConfig) => {
    setEditingConfig({ ...config, smtpPassword: '' });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!editingConfig) return;

    try {
      if (editingConfig._id) {
        const updateData: Partial<MailConfig> = { ...editingConfig };
        if (!updateData.smtpPassword?.trim()) {
          delete updateData.smtpPassword;
        }
        await axios.put(`/api/mail-config/${editingConfig._id}`, updateData);
      } else {
        const createData = {
          ...editingConfig,
          organizerId
        };
        await axios.post('/api/mail-config', createData);
        /*await axios.post('/api/mail-config', {
          ...editingConfig,
          organizerId
        });*/
      }
      
      await fetchMailConfigs();
      setShowDialog(false);
      setEditingConfig(null);
      onMessage(`Mail-Konfiguration erfolgreich ${editingConfig._id ? 'aktualisiert' : 'erstellt'}!`);
    } catch (error: any) {
      onError(`Fehler beim Speichern: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Mail-Konfiguration wirklich löschen?')) return;

    try {
      await axios.delete(`/api/mail-config/${id}`);
      await fetchMailConfigs();
      onMessage('Mail-Konfiguration erfolgreich gelöscht!');
    } catch (error: any) {
      onError(`Fehler beim Löschen: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await axios.put(`/api/mail-config/${id}`, { isDefault: true });
      await fetchMailConfigs();
      onMessage('Standard-Konfiguration erfolgreich gesetzt!');
    } catch (error: any) {
      onError(`Fehler beim Setzen der Standard-Konfiguration: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleTest = async (config: MailConfig) => {
    if (!testEmail.trim()) {
      onError('Bitte geben Sie eine Test-E-Mail-Adresse ein.');
      return;
    }

    if (!config._id) {
      onError('Konfiguration muss zuerst gespeichert werden.');
      return;
    }

    try {
      setTestingConfig(config._id);
      await axios.post(`/api/mail-config/${config._id}/test`, {
        testEmail: testEmail
      });
      onMessage(`Test-E-Mail erfolgreich an ${testEmail} gesendet!`);
      setTestEmail('');
    } catch (error: any) {
      onError(`Test fehlgeschlagen: ${error.response?.data?.error || error.message}`);
    } finally {
      setTestingConfig(null);
    }
  };

  const isFormValid = () => {
    if (!editingConfig) return false;
    return (
      editingConfig.name.trim() &&
      editingConfig.smtpHost.trim() &&
      editingConfig.smtpUser.trim() &&
      editingConfig.fromEmail.trim() &&
      editingConfig.fromName.trim() &&
      (editingConfig._id || (editingConfig.smtpPassword && editingConfig.smtpPassword.trim()))
    );
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Verwalten Sie Ihre E-Mail-Konfigurationen für den Versand von Wettkampf-E-Mails.
      </Typography>

      <Button 
        variant="contained" 
        startIcon={<AddIcon />}
        onClick={handleCreate}
        sx={{ mb: 3 }}
      >
        Neue Konfiguration erstellen
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>SMTP</strong></TableCell>
                <TableCell><strong>Von E-Mail</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Aktionen</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mailConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Keine Mail-Konfigurationen vorhanden
                  </TableCell>
                </TableRow>
              ) : (
                mailConfigs.map((config) => (
                  <TableRow key={config._id} hover>
                    <TableCell>
                      <Box>
                        {config.name}
                        {config.isDefault && (
                          <Chip 
                            label="Standard" 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {config.smtpHost}:{config.smtpPort}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {config.smtpSecure ? 'SSL/TLS' : 'STARTTLS'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{config.fromEmail}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {config.fromName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={config.isActive ? "Aktiv" : "Inaktiv"} 
                        size="small"
                        color={config.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Bearbeiten">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleEdit(config)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Löschen">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(config._id!)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {!config.isDefault && (
                          <Tooltip title="Als Standard setzen">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleSetDefault(config._id!)}
                              sx={{ minWidth: 'auto', px: 1 }}
                            >
                              Standard
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog für Create/Edit */}
      <Dialog 
        open={showDialog} 
        onClose={() => setShowDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {editingConfig?._id ? 'Mail-Konfiguration bearbeiten' : 'Neue Mail-Konfiguration'}
            <IconButton onClick={() => setShowDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {editingConfig && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Grundlegende Einstellungen */}
              <TextField
                label="Name der Konfiguration"
                value={editingConfig.name}
                onChange={(e) => setEditingConfig({...editingConfig, name: e.target.value})}
                fullWidth
                required
                placeholder="z.B. Gmail Wettkampf Account"
              />
              
              {/* SMTP-Einstellungen */}
              <Typography variant="h6" color="primary">SMTP-Server</Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="SMTP Host"
                  value={editingConfig.smtpHost}
                  onChange={(e) => setEditingConfig({...editingConfig, smtpHost: e.target.value})}
                  fullWidth
                  required
                  placeholder="smtp.gmail.com"
                />
                <TextField
                  label="Port"
                  type="number"
                  value={editingConfig.smtpPort}
                  onChange={(e) => setEditingConfig({...editingConfig, smtpPort: parseInt(e.target.value) || 587})}
                  sx={{ width: 120 }}
                  required
                />
              </Box>
              
              <FormControlLabel
                control={
                  <Switch 
                    checked={editingConfig.smtpSecure} 
                    onChange={(e) => setEditingConfig({...editingConfig, smtpSecure: e.target.checked})}
                  />
                }
                label="SSL/TLS verwenden (normalerweise für Port 465)"
              />
              
              <TextField
                label="SMTP Benutzername"
                value={editingConfig.smtpUser}
                onChange={(e) => setEditingConfig({...editingConfig, smtpUser: e.target.value})}
                fullWidth
                required
                placeholder="ihre-email@gmail.com"
              />
              
              <TextField
                label="SMTP Passwort"
                type="password"
                value={editingConfig.smtpPassword}
                onChange={(e) => setEditingConfig({...editingConfig, smtpPassword: e.target.value})}
                fullWidth
                required={!editingConfig._id}
                helperText={editingConfig._id ? 
                  "Leer lassen, um das bestehende Passwort zu behalten" : 
                  "Verwenden Sie bei Gmail ein App-Passwort"
                }
              />
              
              {/* Absender-Konfiguration */}
              <Typography variant="h6" color="primary">Absender-Einstellungen</Typography>
              
              <TextField
                label="Absender E-Mail-Adresse"
                type="email"
                value={editingConfig.fromEmail}
                onChange={(e) => setEditingConfig({...editingConfig, fromEmail: e.target.value})}
                fullWidth
                required
                placeholder="wettkampf@verein.de"
              />
              
              <TextField
                label="Absender Name"
                value={editingConfig.fromName}
                onChange={(e) => setEditingConfig({...editingConfig, fromName: e.target.value})}
                fullWidth
                required
                placeholder="TSV Musterverein"
              />
              
              <TextField
                label="Antwort-Adresse (optional)"
                type="email"
                value={editingConfig.replyTo || ''}
                onChange={(e) => setEditingConfig({...editingConfig, replyTo: e.target.value})}
                fullWidth
                placeholder="wettkampf@verein.de"
                helperText="Falls abweichend von der Absender-Adresse"
              />
              
              {/* Status-Einstellungen */}
              <Typography variant="h6" color="primary">Status</Typography>
              
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={editingConfig.isActive} 
                      onChange={(e) => setEditingConfig({...editingConfig, isActive: e.target.checked})}
                    />
                  }
                  label="Konfiguration aktivieren"
                />
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={editingConfig.isDefault} 
                      onChange={(e) => setEditingConfig({...editingConfig, isDefault: e.target.checked})}
                    />
                  }
                  label="Als Standard-Konfiguration verwenden"
                />
              </Box>

              {/* Test-Bereich */}
              {editingConfig._id && (
                <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Konfiguration testen
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <TextField
                      label="Test E-Mail-Adresse"
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      fullWidth
                      placeholder="test@example.com"
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => handleTest(editingConfig)}
                      disabled={testingConfig === editingConfig._id || !testEmail.trim()}
                      startIcon={
                        testingConfig === editingConfig._id ? 
                          <CircularProgress size={16} /> : 
                          <TestIcon />
                      }
                    >
                      Test senden
                    </Button>
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowDialog(false)}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={!isFormValid()}
          >
            {editingConfig?._id ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MailConfigTab;